import { useState, createRef } from 'react';
import PropTypes from 'prop-types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  IconButton,
  Tooltip
} from '@material-ui/core';
import Scrollbar from '../Scrollbar';
import SearchNotFound from '../SearchNotFound';
import { UserListHead, UserListToolbar } from '../user';
import { RemoveUserButton } from './team';
import { applySortFilter, getComparator } from '../../utils/sort';

const defaultTableHead = [{ id: 'email', label: 'Email', alignRight: false }, { id: '' }];

UserTable.propTypes = {
  users: PropTypes.array.isRequired,
  variant: PropTypes.oneOf(['admin', 'team']).isRequired,
  // Admin variant props
  openModal: PropTypes.func,
  setModalUser: PropTypes.func,
  // Team variant props
  updateUsers: PropTypes.func,
  team: PropTypes.string,
  // Common props
  tableHead: PropTypes.array,
  placeholder: PropTypes.string,
  showCard: PropTypes.bool,
  maxHeight: PropTypes.number,
  minWidth: PropTypes.number,
  stickyHeader: PropTypes.bool,
  size: PropTypes.string,
  enableScrollToTop: PropTypes.bool
};

UserTable.defaultProps = {
  tableHead: defaultTableHead,
  placeholder: 'Search users...',
  showCard: true,
  stickyHeader: false,
  size: 'medium',
  enableScrollToTop: false
};

export default function UserTable({
  users,
  variant,
  openModal,
  setModalUser,
  updateUsers,
  team,
  tableHead,
  placeholder,
  showCard,
  maxHeight,
  minWidth,
  stickyHeader,
  size,
  enableScrollToTop
}) {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const tableRef = createRef();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (enableScrollToTop && tableRef.current) {
      tableRef.current.scrollIntoView();
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    if (variant === 'team') {
      setPage(0);
    }
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0;
  const filteredUsers = applySortFilter(users, getComparator(order), filterName);
  const isUserNotFound = filteredUsers.length === 0;

  const renderActionButton = (row) => {
    if (variant === 'admin') {
      return (
        <Tooltip title="User Info">
          <IconButton
            onClick={() => {
              setModalUser(row);
              openModal(true);
            }}
            color="primary"
          >
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      );
    }

    if (variant === 'team') {
      return (
        <RemoveUserButton updateTable={updateUsers} user={row} team={team} variant="icononly" />
      );
    }

    return null;
  };

  const getToolbarProps = () => {
    const baseProps = {
      placeholder,
      filterName,
      onFilterName: handleFilterByName
    };

    if (variant === 'team') {
      return {
        ...baseProps,
        disableGutters: true,
        variant: 'dense',
        sx: { margin: 0 }
      };
    }

    return baseProps;
  };

  const getTableContainerProps = () => {
    const baseProps = {
      'data-testid': 'user-table'
    };

    if (maxHeight) {
      baseProps.sx = { maxHeight };
    } else if (minWidth) {
      baseProps.sx = { minWidth };
    }

    return baseProps;
  };

  const tableContent = (
    <>
      <UserListToolbar {...getToolbarProps()} />

      <TableContainer {...getTableContainerProps()}>
        <Table
          ref={enableScrollToTop ? tableRef : undefined}
          stickyHeader={stickyHeader}
          size={size}
        >
          <UserListHead
            order={order}
            orderBy={orderBy}
            headLabel={tableHead}
            rowCount={users.length}
            onRequestSort={handleRequestSort}
          />

          <TableBody>
            {filteredUsers != null &&
              filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, i) => (
                  <TableRow hover key={i} tabIndex={-1}>
                    <TableCell align="left">{row}</TableCell>
                    <TableCell align="right">{renderActionButton(row)}</TableCell>
                  </TableRow>
                ))}

            {variant === 'admin' && emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>

          {isUserNotFound && (
            <TableBody>
              <TableRow>
                <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                  <SearchNotFound searchQuery={filterName} />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={users.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );

  if (variant === 'admin' && showCard) {
    return (
      <Card>
        <Scrollbar>{tableContent}</Scrollbar>
      </Card>
    );
  }

  return tableContent;
}
